import {
  Configuration,
  DataItem,
  Dimensions,
  Gutter,
  OptionsMaterial,
  Params,
  Parapet,
  Roof,
  Wall,
} from "src/types/configuration";
import ConfiguratorService from "src/services/configuratorService";
import prices from "src/prices";
import { calculateKapschuurRoofBigSideLength } from "src/helpers/configuration";
import { useConfiguration } from "src/contexts/ConfigurationContext";

class CalculationService {
  private static instance: CalculationService;
  private configuratorService?: ConfiguratorService;
  private state?: Configuration;
  private dimensions?: Dimensions;
  private parapet?: Parapet;
  private roof?: Roof;
  private roofOverhang = 0;
  private gutter?: Gutter;
  private walls?: Wall[];
  private storages?: { left: boolean; right: boolean };
  private data: DataItem[] = [];
  private optionsMaterial: OptionsMaterial | undefined;
  private params: Params | undefined;

  constructor(
    initConfiguration: Configuration,
    configuratorService: ConfiguratorService
  ) {
    if (CalculationService.instance) {
      return CalculationService.instance;
    }

    CalculationService.instance = this;
    this.state = initConfiguration;
    this.configuratorService = configuratorService;
    this.dimensions = initConfiguration.dimensions;
    // this.parapet = initConfiguration.parapet;
    this.roof = initConfiguration.roof;
    this.gutter = initConfiguration.gutter;
    this.walls = initConfiguration.walls;
    // this.storages = initConfiguration.storages;
    this.optionsMaterial = initConfiguration.optionsMaterial;
    this.params = initConfiguration.params;

    this.data = prices
      .filter((price) => price && price.price > 0)
      .map((p) => {
        return {
          name: p.name,
          label: p.label,
          unit: p.unit,
          price: p.price,
          quantity: 0,
          total: 0,
        };
      });
  }

  //ok
  setState(state: Configuration): void {
    this.state = state;
    this.dimensions = state.dimensions;
    // this.parapet = state.parapet;
    this.roof = state.roof;
    this.gutter = state.gutter;
    this.walls = state.walls;
    // this.storages = state.storages;
    this.optionsMaterial = state.optionsMaterial;
    this.params = state.params;
  }

  getData() {
    return this.data.filter((item) => {
      if (!item) return false;

      // @ts-ignore
      if (this.state.roofType === "platdak") {
        if (item.name === "dakpannen" || item.name === "dakpannen_terracota") {
          return false;
        }
      }

      return true;
    });
  }

  //   isKapschuur() {
  //     return this.state?.roofType === "kapschuur";
  //   }

  getEPDMGlue() {
    const item = this.getPriceItem("epdmGlue");
    return item ? item.price * this.calculatePlatdakRoofSquareInSqMeters() : 0;
  }

  getBlackTrim() {
    const item = this.getPriceItem("daktrim_zwart");
    return item ? item.price * this.getRoofPerimeterInMeters() : 0;
  }

  getAluminiumTrim() {
    const item = this.getPriceItem("daktrim_blank");
    return item ? item.price * this.getRoofPerimeterInMeters() : 0;
  }

  getPoer(name: string) {
    const item = this.getPriceItem(name);
    if (!item) return 0;
    // if (!this.optionsMaterial?.poerQuantity) return item.price;
    const quantity = window.CONF.house.columns.length / 2;
    return item.price * quantity;
  }

  getLight(name: string) {
    if (name === "all" || name === "some") {
      const item = this.getPriceItem("spotlight");
      if (!item) return 0;
      const oase = this.state?.roofType === "oase";
      const houseWidth = (this.dimensions?.width ?? 0) / 1000;
      const glassCheck = this.state?.roofGlassMat.glass === "glass";
      const everyLigger = name === "all";
      const gap = 0.012;
      const liggerWidth = 0.058;
      const houseColumnSize = oase ? 0.11 : 0.15;
      const maxPanelWidth = glassCheck ? 0.8 : 1.2;

      const posx = oase ? liggerWidth / 2 : houseColumnSize + liggerWidth / 2;
      const lastposx = oase
        ? houseWidth - liggerWidth / 2
        : houseWidth - houseColumnSize - liggerWidth / 2;
      const distance = lastposx - posx;
      let amount = Math.trunc(distance / (maxPanelWidth + gap)) - 1;
      if (distance / amount > maxPanelWidth) amount += 1;

      const quantity = everyLigger ? amount * 2 : amount - (amount % 2);
      return quantity * item.price;
    } else return 0;
  }

  getSpie(left = false, right = false) {
    if (!left && !right) return 0;
    let spyquantity = 0;
    if (left) spyquantity++;
    if (right) spyquantity++;

    const frame = this.getPriceItem("spyFrame");
    const polycarbonatSpy = this.getPriceItem("polycarbonatSpy");
    const polycarbonatWideSpy = this.getPriceItem("polycarbonatWideSpy");
    const glassSpy = this.getPriceItem("glassSpy");
    if (!frame || !polycarbonatSpy || !polycarbonatWideSpy || !glassSpy)
      return 0;

    const houseDepth = (this.dimensions?.depth ?? 0) / 1000;
    const glassCheck = this.state?.roofGlassMat.glass === "glass";

    const polyWide = this.state?.roofGlassMat.glass === "polywide";
    const poly = this.state?.roofGlassMat.glass === "poly";

    const spiearea =
      Math.tan((8 * Math.PI) / 180) * (houseDepth - 0.11) * (houseDepth - 0.11);
    const spieperimeter =
      houseDepth -
      0.11 +
      (houseDepth - 0.11) * Math.tan(8 * (Math.PI / 180)) +
      (houseDepth - 0.11) / Math.cos(8 * (Math.PI / 180));

    const roundUpToNearestHalf = (num: number): number =>
      Math.ceil(num * 2) / 2;

    let quantity = 0;
    let total = 0;

    total += spyquantity * roundUpToNearestHalf(spieperimeter) * frame.price;
    quantity = poly ? spyquantity * roundUpToNearestHalf(spiearea) : 0;
    total += quantity * polycarbonatSpy.price;
    quantity = polyWide ? spyquantity * roundUpToNearestHalf(spiearea) : 0;
    total += quantity * polycarbonatWideSpy.price;
    quantity = glassCheck ? spyquantity * roundUpToNearestHalf(spiearea) : 0;
    total += quantity * glassSpy.price;

    return total;
  }

  getSunscreen(name: string) {
    const sunscreentop = this.state?.sunScreen.top;
    const houseDepth = (this.dimensions?.depth ?? 0) / 1000;
    const houseWidth = (this.dimensions?.width ?? 0) / 1000;
    const oase = this.state?.roofType === "oase";
    const aludra = this.state?.roofType === "aludra";
    const houseColumnSize = oase ? 0.11 : 0.15;
    const roundUpToNearestHalf = (num: number): number =>
      Math.ceil(num * 2) / 2;
    const length = oase
      ? roundUpToNearestHalf(
          (houseDepth - houseColumnSize) / Math.cos((8 * Math.PI) / 180)
        )
      : roundUpToNearestHalf(houseDepth - houseColumnSize);

    let total = 0;
    const parts = [
      "sunScreenBody",
      "sunScreenSideGuide",
      "sunScreenGuideMountingBracketsBottom",
      "sunScreenGuideMountingBracketsTop",
      "sunScreenGuideMountingBracketsTopOase",
      "sunScreenTextile",
      "sunScreenMolding",
    ]
      .map((part) => this.getPriceItem(part))
      .map((item) => {
        if (!item) return 0;
        let quantity = 0;
        if (item.name === "sunScreenBody") {
          quantity = 1;
          total += quantity * item.price * roundUpToNearestHalf(houseWidth);
        }
        if (item.name === "sunScreenSideGuide") {
          quantity = 2;
          total += quantity * item.price * length;
        }
        if (item.name === "sunScreenGuideMountingBracketsBottom") {
          quantity = !sunscreentop ? 4 : 0;
          total += quantity * item.price;
        }
        if (item.name === "sunScreenGuideMountingBracketsTop") {
          quantity = aludra && sunscreentop ? 4 : 0;
          total += quantity * item.price;
        }
        if (item.name === "sunScreenGuideMountingBracketsTopOase") {
          quantity = oase && sunscreentop ? 4 : 0;
          total += quantity * item.price;
        }
        if (item.name === "sunScreenTextile") {
          //fix me later
          quantity = 1;
          total += quantity * item.price * houseWidth * length;
        }
        if (item.name === "sunScreenMolding") {
          quantity = 1;
          total +=
            quantity *
            item.price *
            roundUpToNearestHalf(houseWidth - houseColumnSize * 2);
        }
      });

    return total;
  }

  getSlidingDoorsPrice(length: number, type: string = "light") {
    let amount = 0;
    const item =
      type === "dark"
        ? this.getPriceItem("glassdoorsdark")
        : this.getPriceItem("glassdoors");
    if (!item) return 0;

    if (length >= 6250) {
      amount = 7;
    } else if (length >= 5250) {
      amount = 6;
    } else if (length >= 4250) {
      amount = 5;
    } else if (length >= 3250) {
      amount = 4;
    } else if (length >= 2240) {
      amount = 3;
    } else {
      amount = 3;
    }

    return amount * (item?.price ? item.price : 0);
  }

  getWindowsPrice(lenght: number, type: string = "light") {
    let amount = lenght > 2500 ? 4 : 2;
    const item =
      type === "dark"
        ? this.getPriceItem("glassdoorsdark")
        : this.getPriceItem("glassdoors");
    return amount * (item?.price ? item.price : 0);
  }

  GetAlum(length: number, isLeft: boolean = false, isRight: boolean = false) {
    const item = this.getPriceItem("aluminiumWall");
    if (!item) return 0;
    const oase = this.state?.roofType === "oase";
    const depth = this.dimensions?.depth ?? 0;
    const height = this.dimensions?.height ?? 0;
    const houseDepth = depth / 1000;
    const houseHeight = height / 1000;
    let quantity = 1 * ((length / 1000) * houseHeight);
    const spyleft = this.state?.spy.left;
    const spyright = this.state?.spy.right;

    const spiearea =
      Math.tan((8 * Math.PI) / 180) *
      (depth / 1000 - 0.11) *
      (houseDepth - 0.11);

    const roundUpToNearestHalf = (num: number): number =>
      Math.ceil(num * 2) / 2;

    if (oase)
      if ((isLeft && !spyleft) || (isRight && !spyright)) quantity += spiearea;

    quantity = roundUpToNearestHalf(quantity);

    return item.price * quantity;
  }

  getFundering(name: string) {
    const item = this.getPriceItem(name);
    if (!item) return 0;
    let quantity = 0;
    let total = 0;
    const truewalls = window.CONF.house.walls.map((item) => {
      return [
        item.sections[0].type,
        item.sections[0].length / 1000,
        item.sections[0].customization.includes("glassSectionDark"),
      ];
    });

    const roundUpToNearestHalf = (num: number): number =>
      Math.ceil(num * 2) / 2;

    truewalls.map((item) => {
      if (item[0].startsWith("door") || item[0].startsWith("window"))
        quantity += roundUpToNearestHalf(item[1]);
    });
    total = item.price * quantity;
    return total;
  }

  getPoertegel() {
    const item = this.getPriceItem("betontegel");
    if (!item) return 0;
    if (!this.optionsMaterial?.poerQuantity) return item.price;
    const quantity =
      this.optionsMaterial.poerQuantity > 0 &&
      this.optionsMaterial.poer === "poertegel"
        ? this.optionsMaterial.poerQuantity
        : 1;
    return item.price * quantity;
  }

  getLiterBeitsWit() {
    const item = this.getPriceItem("liter_beits_wit");
    if (!item) return 0;
    if (!this.optionsMaterial?.liter_beits_wit) return item.price;
    const quantity =
      this.optionsMaterial.liter_beits_wit > 0
        ? this.optionsMaterial.liter_beits_wit
        : 1;
    return item ? item.price * quantity : 0;
  }

  getLiterBeitsZwart() {
    const item = this.getPriceItem("liter_beits_zwart");
    if (!item) return 0;
    if (!this.optionsMaterial?.liter_beits_zwart) return item.price;
    const quantity =
      this.optionsMaterial.liter_beits_zwart > 0
        ? this.optionsMaterial.liter_beits_zwart
        : 1;
    return item.price * quantity;
  }

  getLiterBeitsGray() {
    const item = this.getPriceItem("liter_beits_gray");
    if (!item) return 0;
    if (!this.optionsMaterial?.liter_beits_gray) return item.price;
    const quantity =
      this.optionsMaterial.liter_beits_gray > 0
        ? this.optionsMaterial.liter_beits_gray
        : 1;
    return item.price * quantity;
  }

  getLiterBeitsDouglas() {
    const item = this.getPriceItem("liter_beits_douglas");
    if (!item) return 0;
    if (!this.optionsMaterial?.liter_beits_douglas) return item.price;
    const quantity =
      this.optionsMaterial.liter_beits_douglas > 0
        ? this.optionsMaterial.liter_beits_douglas
        : 1;
    return item.price * quantity;
  }

  getRoofingPrice(name: string) {
    if (!this.data || !this.state) return 0;

    const kapschuurDepthRoofSide =
      this.state.params.kapschuurOverhangLength +
      calculateKapschuurRoofBigSideLength(this.state);
    const width = this.dimensions?.width ?? 0;
    const quantity = this.toSqMeter(width * kapschuurDepthRoofSide);
    let price = 0;

    if (name === "dakpanplaat") {
      price = this.getPriceItem("dakpanprofielen-zwart")?.price ?? 0;
    }

    if (name === "dakpanplaat_rood") {
      price = this.getPriceItem("dakpanprofielen-terracotta")?.price ?? 0;
    }

    if (name === "leiendak") {
      price = this.getPriceItem("shingles-zwart")?.price ?? 0;
    }

    if (name === "leiendak_red") {
      price = this.getPriceItem("shingles-rood")?.price ?? 0;
    }

    if (name === "leiendak_green") {
      price = this.getPriceItem("shingles-groen")?.price ?? 0;
    }

    return price * quantity;
  }

  getGutter(name: string): number {
    const houseWidth = this.state!.dimensions.width / 1000;

    const roundUpToNearestHalf = (num: number): number =>
      Math.ceil(num * 2) / 2;

    let total = 0;

    if (name === "default") {
      // Gutter type "default"
      // Gutter body: "gootprofiel"
      // Gutter cap: "afdekkapGootprofiel"
      const gutterBody = prices.find((item) => item.name === "gootprofiel");
      const gutterCap = prices.find(
        (item) => item.name === "afdekkapGootprofiel"
      );

      if (gutterBody) {
        total += 1 * gutterBody.price * roundUpToNearestHalf(houseWidth);
      }
      if (gutterCap) {
        total += 2 * gutterCap.price;
      }
    } else if (name === "rond") {
      // Gutter type "rond"
      // Gutter body: "gootsierlijstRond"
      // Gutter cap: "gootcapRond"
      const gutterBody = prices.find(
        (item) => item.name === "gootsierlijstRond"
      );
      const gutterCap = prices.find((item) => item.name === "gootcapRond");

      if (gutterBody) {
        total += 1 * gutterBody.price * roundUpToNearestHalf(houseWidth);
      }
      if (gutterCap) {
        total += 2 * gutterCap.price;
      }
    } else if (name === "klassiek") {
      // Gutter type "klassiek"
      // Gutter body: "gootsierlijstKlassiek"
      // Gutter cap: "gootcapKlassiek"
      const gutterBody = prices.find(
        (item) => item.name === "gootsierlijstKlassiek"
      );
      const gutterCap = prices.find((item) => item.name === "gootcapKlassiek");

      if (gutterBody) {
        total += 1 * gutterBody.price * roundUpToNearestHalf(houseWidth);
      }
      if (gutterCap) {
        total += 2 * gutterCap.price;
      }
    }

    return total;
  }

  getGlass(name: string): number {
    const currentRoofType = this.state?.roofType;

    const depth = this.dimensions?.depth ?? 0;
    const width = this.dimensions?.width ?? 0;
    const houseDepth = depth / 1000;
    const houseWidth = width / 1000;

    const roundUpToNearestHalf = (num: number): number =>
      Math.ceil(num * 2) / 2;

    const posx = currentRoofType === "aludra" ? 0.179 : 0.029;
    const gap = 0.012;
    const maxPanelWidth = name.startsWith("poly") ? 1.2 : 0.8;

    let amount =
      Math.trunc((houseWidth - posx * 2) / (maxPanelWidth + gap)) + 2;
    if ((houseWidth - posx * 2) / amount > maxPanelWidth) amount += 1;
    const quantity = amount - 1;

    const item = prices.find((p) => p.name === name);
    if (!item) return 0;

    if (currentRoofType === "aludra") {
      return quantity * item.price * roundUpToNearestHalf(houseDepth);
    } else if (currentRoofType === "oase") {
      const houseColumnSize = 0.11;
      const liggerLength =
        (houseDepth - houseColumnSize) / Math.cos((8 * Math.PI) / 180);
      const ligtoHalfLength = roundUpToNearestHalf(liggerLength);

      return quantity * item.price * ligtoHalfLength;
    }

    return 0;
  }

  roundValue(num: number, precision: number = 2) {
    return parseFloat(num.toFixed(precision));
  }

  formatToCurrency(price: number) {
    return price.toLocaleString("nl-NL", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  calculateTotalPrice(): number {
    return this.data.reduce((acc, item) => {
      if (!item || item.quantity === 0) return acc;
      return acc + item.total;
    }, 0);
  }

  getFormatTotalPrice(): string {
    return this.formatToCurrency(this.calculateTotalPrice());
  }

  toMeter = (value: number): number => {
    const meters = value / 1000;

    return parseFloat(meters.toFixed(2));
  };

  toSqMeter(value: number): number {
    const meters = value / 1_000_000;

    return parseFloat(meters.toFixed(2));
  }

  calculateRoofSideLength() {
    const base = this.dimensions?.depth ?? 0;
    const angle = this.dimensions?.angle ?? 8;
    const angleInRadians = (angle * Math.PI) / 180;
    const sideLength =
      (base + this.roofOverhang) / (2 * Math.cos(angleInRadians));

    return parseFloat(sideLength.toFixed(2));
  }

  calculateRoofSquareInSqMeters() {
    const width = this.dimensions?.width ?? 0;
    const sideLength = this.calculateRoofSideLength();

    return this.toSqMeter(2 * sideLength * (width + this.roofOverhang));
  }

  calculatePlatdakRoofSquareInSqMeters() {
    const width = this.getWidthWithOverhang();
    const depth = this.getDepthWithOverhang();
    return this.toSqMeter(width * depth);
  }

  calculateTriangleAreaInSqMeters() {
    const base = this.dimensions?.depth ?? 0;
    const sideLength = this.calculateRoofSideLength();

    return this.toSqMeter(
      (1 / 2) * base * Math.sqrt(4 * sideLength ** 2 - base ** 2)
    );
  }

  getCountVerticalBalks() {
    return this.configuratorService?.getColumnsBalks() ?? 0;
  }

  getWidthWithOverhang() {
    const width = this.dimensions?.width ?? 0;
    const overhangLeft = this.dimensions?.overhangLeft ?? 0;
    const overhangRight = this.dimensions?.overhangRight ?? 0;
    return width + overhangLeft + overhangRight;
  }

  getDepthWithOverhang() {
    const depth = this.dimensions?.depth ?? 0;
    const overhangBack = this.dimensions?.overhangBack ?? 0;
    const overhangFront = this.dimensions?.overhangFront ?? 0;
    return depth + overhangBack + overhangFront;
  }

  getRoofPerimeterInMeters() {
    const width = this.getWidthWithOverhang() * 2;
    const depth = this.getDepthWithOverhang() * 2;
    return this.toMeter(width + depth);
  }

  getPriceItemFromData(key: string) {
    return this.data.find((item) => item && item.name === key);
  }

  getPriceItem(key: string) {
    return prices.find((item) => item && item.name === key);
  }

  calculateConfiguration(): DataItem[] {
    if (!this.data || !this.state) return [];

    const columns = window.CONF.house.columns.length / 2;
    const width = this.dimensions?.width ?? 0;
    // const widthWithOverhang =
    //   width +
    //   (this.dimensions?.overhangLeft ?? 0) +
    //   (this.dimensions?.overhangRight ?? 0);
    const depth = this.dimensions?.depth ?? 0;
    const height = this.dimensions?.height ?? 0;

    // const depthWithOverhang =
    //   depth +
    //   (this.dimensions?.overhangBack ?? 0) +
    //   (this.dimensions?.overhangFront ?? 0);
    // const height = this.dimensions?.height ?? 0;
    // const overhangLeft = this.dimensions?.overhangLeft ?? 0;
    // const overhangRight = this.dimensions?.overhangRight ?? 0;
    // const literBeitsWit = this.optionsMaterial?.liter_beits_wit ?? 0;
    // const literBeitsZwart = this.optionsMaterial?.liter_beits_zwart ?? 0;
    // const literBeitsGray = this.optionsMaterial?.liter_beits_gray ?? 0;
    // const literBeitsDouglas = this.optionsMaterial?.liter_beits_douglas ?? 0;
    // const daktrim_blank = this.optionsMaterial?.daktrim_blank ?? 0;
    // const daktrim_zwart = this.optionsMaterial?.daktrim_zwart ?? 0;
    // const kapschuurDepthRoofSide =
    // this.state.params.kapschuurOverhangLength +
    // calculateKapschuurRoofBigSideLength(this.state);
    // const isCommonWall =
    //   this.storages?.left && this.storages?.right ? columns === 2 : false;

    const truewalls = window.CONF.house.walls.map((item) => {
      return [
        item.sections[0].type,
        item.sections[0].length / 1000,
        item.sections[0].customization.includes("glassSectionDark"),
      ];
    });

    const roundUpToNearestHalf = (number: number) => {
      return Math.ceil(number * 2) / 2;
    };

    const roundUp = (number: number) => {
      return Math.ceil(number);
    };

    const roundUpToCm = (number: number) => {
      return Math.ceil(number * 100) / 100;
    };

    this.data = prices.map((item) => {
      if (!item) return item;

      const oase = this.state?.roofType === "oase";
      const aludra = this.state?.roofType === "aludra";
      const sunscreen = this.state?.sunScreen.enabled;
      const lights = this.state?.lights.enabled;

      const glassCheck = this.state?.roofGlassMat.glass === "glass";
      const glassDarkCheck = this.state?.roofGlassMat.glassshade === "dark";
      const polyWide = this.state?.roofGlassMat.glass === "polywide";
      const poly = this.state?.roofGlassMat.glass === "poly";
      const polylightCheck = this.state?.roofGlassMat.polyshade === "light";
      const polymidCheck = this.state?.roofGlassMat.polyshade === "mid";
      const polydarkCheck = this.state?.roofGlassMat.polyshade === "dark";

      const poer = this.state?.optionsMaterial.poer === "poer";
      const hwapoer = this.state?.optionsMaterial.poer === "hwapoer";

      const houseWidth = width / 1000;
      const houseDepth = depth / 1000;
      const houseHeight = height / 1000;

      const liggerWidth = 0.058;
      const gap = 0.012;
      const maxPanelWidth = glassCheck ? 0.8 : 1.2;
      const houseColumnSize = oase ? 0.11 : 0.15;
      // const spiearea =
      //   0.5 * Math.pow(houseDepth - 0.11, 2) * Math.tan(8 * (Math.PI / 180));
      const spiearea =
        Math.tan((8 * Math.PI) / 180) *
        (depth / 1000 - 0.11) *
        (houseDepth - 0.11);
      const spieperimeter =
        houseDepth -
        0.11 +
        (houseDepth - 0.11) * Math.tan(8 * (Math.PI / 180)) +
        (houseDepth - 0.11) / Math.cos(8 * (Math.PI / 180));

      // console.log(`v-0-${columns - 1}`);

      let quantity = 0;
      let total = 0;

      const liggerLength =
        (houseDepth - houseColumnSize) / Math.cos((8 * Math.PI) / 180);

      const ligtoHalfLength = roundUpToNearestHalf(liggerLength);
      const ligtoOneLength = roundUp(liggerLength);

      if (aludra) {
        const posx = houseColumnSize + liggerWidth / 2;
        const lastposx = houseWidth - houseColumnSize - liggerWidth / 2;
        const distance = lastposx - posx;

        let amount = Math.trunc(distance / (maxPanelWidth + gap)) + 2;

        //columns
        if (item.name === "staander_150x150") {
          quantity =
            (this.getCountVerticalBalks() / 2) *
            roundUpToNearestHalf(houseHeight);
          total = quantity * item.price;
        }

        //roof parts
        if (item.name === "goot") {
          quantity = 1 * roundUpToNearestHalf(houseWidth);
          total = quantity * item.price;
        }
        if (item.name === "afdekkapGootprofiel") {
          quantity = 2;
          total = quantity * item.price;
        }
        if (item.name === "ligger_165x200") {
          quantity = 2 * roundUpToNearestHalf(houseDepth);
          total = quantity * item.price;
        }
        if (item.name === "ligger") {
          quantity =
            (amount < 3 ? 3 : amount) * roundUpToNearestHalf(houseDepth);
          total = quantity * item.price;
        }
        if (item.name === "middenAfdekker") {
          quantity = (amount - 2) * roundUpToNearestHalf(houseDepth);
          total = quantity * item.price;
        }
        if (item.name === "zijAfdekker") {
          quantity = 2 * ligtoHalfLength;
          total = quantity * item.price;
        }
        if (item.name === "plaatstopper") {
          quantity =
            amount *
            roundUpToNearestHalf(houseWidth / (glassCheck ? amount : amount));
          total = quantity * item.price;
        }
        if (item.name === "muurplaat") {
          quantity = 1 * roundUpToNearestHalf(houseWidth);
          total = quantity * item.price;
        }
        if (item.name === "afdekkapmuurprofiel") {
          quantity = 2;
          total = quantity * item.price;
        }
        if (item.name === "condensprofiel") {
          quantity = (amount - 1) * roundUp((houseWidth * 100) / (amount - 1));
          total = quantity * item.price;
        }
        if (item.name === "kliklijst") {
          quantity = (amount - 1) * 2;
          total = quantity * item.price;
        }

        //glass

        const glassAmount =
          (amount - 1) *
          roundUpToNearestHalf(
            houseDepth * roundUpToCm(houseWidth / (amount - 1))
          );

        if (item.name === "glass") {
          quantity = glassCheck && !glassDarkCheck ? glassAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "glassdark") {
          quantity = glassCheck && glassDarkCheck ? glassAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "polylight") {
          quantity = poly && polylightCheck ? glassAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "polymid") {
          quantity = poly && polymidCheck ? glassAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "polydark") {
          quantity = poly && polydarkCheck ? glassAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "polywide") {
          quantity = polyWide ? glassAmount : 0;
          total = quantity * item.price;
        }
      }
      if (oase) {
        const rond = this.state?.gutter.type === "rond";
        const klassiek = this.state?.gutter.type === "klassiek";
        const defaultgoot = this.state?.gutter.type === "default";

        const spyleft = this.state?.spy.left;
        const spyright = this.state?.spy.right;

        const posx = liggerWidth / 2;
        const lastposx = houseWidth - liggerWidth / 2;
        const distance = lastposx - posx;

        let amount = Math.trunc(distance / (maxPanelWidth + gap)) + 2;
        if (distance / amount > maxPanelWidth) amount += 1;

        //columns
        if (item.name === "standdeer_110x110") {
          quantity =
            (this.getCountVerticalBalks() / 2) *
            roundUpToNearestHalf(houseHeight);
          total = quantity * item.price;
        }

        //roof parts
        if (item.name === "gootprofiel") {
          quantity = 1 * roundUpToNearestHalf(houseWidth);
          total = quantity * item.price;
        }
        if (item.name === "afdekkapGootprofiel") {
          quantity = defaultgoot ? 2 : 0;
          total = quantity * item.price;
        }
        if (item.name === "zijAfdekker") {
          quantity = 2 * ligtoHalfLength;
          total = quantity * item.price;
        }
        if (item.name === "middenAfdekkerOase") {
          quantity = (amount - 2) * ligtoHalfLength;
          total = quantity * item.price;
        }
        if (item.name === "liggerOase") {
          quantity = (amount < 3 ? 3 : amount) * ligtoHalfLength;
          total = quantity * item.price;
        }
        if (item.name === "muurplaatOase") {
          quantity = 1 * roundUpToNearestHalf(houseWidth);
          total = quantity * item.price;
        }
        if (item.name === "afdekkapmuurprofiel") {
          quantity = 2;
          total = quantity * item.price;
        }
        if (item.name === "gootsierlijstKlassiek") {
          quantity = klassiek ? 1 * roundUpToNearestHalf(houseWidth) : 0;
          total = quantity * item.price;
        }
        if (item.name === "gootcapKlassiek") {
          quantity = klassiek ? 2 : 0;
          total = quantity * item.price * 1;
        }
        if (item.name === "gootsierlijstRond") {
          quantity = rond ? 1 * roundUpToNearestHalf(houseWidth) : 0;
          total = quantity * item.price;
        }
        if (item.name === "gootcapRond") {
          quantity = rond ? 2 : 0;
          total = quantity * item.price;
        }
        if (item.name === "condensprofielOase") {
          quantity = (amount - 1) * roundUp((houseWidth * 100) / (amount - 1));
          total = quantity * item.price;
        }
        if (item.name === "oaseplaatstopper") {
          quantity = amount;
          total = quantity * item.price;
        }
        if (item.name === "kliklijst") {
          quantity = (amount - 1) * 2;
          total = quantity * item.price;
        }

        //glass

        const glassAmount =
          (amount - 1) *
          roundUpToNearestHalf(
            ligtoHalfLength * roundUpToCm(houseWidth / (amount - 1))
          );

        if (item.name === "glass") {
          quantity = glassCheck && !glassDarkCheck ? glassAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "glassdark") {
          quantity = glassCheck && glassDarkCheck ? glassAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "polylight") {
          quantity = poly && polylightCheck ? glassAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "polymid") {
          quantity = poly && polymidCheck ? glassAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "polydark") {
          quantity = poly && polydarkCheck ? glassAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "polywide") {
          quantity = polyWide ? glassAmount : 0;
          total = quantity * item.price;
        }

        //spy
        if (spyleft || spyright) {
          let spyquantity = 0;
          if (spyleft) spyquantity++;
          if (spyright) spyquantity++;

          if (item.name === "spyFrame") {
            quantity = spyquantity * roundUpToNearestHalf(spieperimeter);
            total = quantity * item.price;
            console.log(spiearea);
          }
          if (item.name === "polycarbonatSpy") {
            quantity = poly ? spyquantity * roundUpToNearestHalf(spiearea) : 0;
            total = quantity * item.price;
          }
          if (item.name === "polycarbonatWideSpy") {
            quantity = polyWide
              ? spyquantity * roundUpToNearestHalf(spiearea)
              : 0;
            total = quantity * item.price;
          }
          if (item.name === "glassSpy") {
            quantity = glassCheck
              ? spyquantity * roundUpToNearestHalf(spiearea)
              : 0;
            total = quantity * item.price;
          }
        }
      }
      if (sunscreen) {
        const sunscreentop = this.state?.sunScreen.top;

        if (item.name === "sunScreenBody") {
          quantity = 1;
          total = quantity * item.price * roundUpToNearestHalf(houseWidth);
          // total = quantity * item.price * roundUpToNearestHalf(houseDepth);
        }
        if (item.name === "sunScreenSideGuide") {
          quantity = 2;
          total =
            quantity *
            item.price *
            roundUpToNearestHalf(houseDepth - houseColumnSize);
        }
        if (item.name === "sunScreenGuideMountingBracketsBottom") {
          quantity = !sunscreentop ? 4 : 0;
          total = quantity * item.price;
        }
        if (item.name === "sunScreenGuideMountingBracketsTop") {
          quantity = aludra && sunscreentop ? 4 : 0;
          total = quantity * item.price;
        }
        if (item.name === "sunScreenGuideMountingBracketsTopOase") {
          quantity = oase && sunscreentop ? 4 : 0;
          total = quantity * item.price;
        }
        if (item.name === "sunScreenTextile") {
          //fix me later
          quantity = 1;
          total = quantity * item.price * houseWidth * houseDepth;
        }
        if (item.name === "sunScreenMolding") {
          quantity = 1;
          total =
            quantity *
            item.price *
            roundUpToNearestHalf(houseWidth - houseColumnSize * 2);
        }
      }
      //walls separation
      if (true) {
        if (item.name === "glassdoors") {
          truewalls.map((wall) => {
            if (!wall[2]) {
              if (wall[0] === "door_3x3Tracks") quantity += 3;
              else if (wall[0] === "door_4x4Tracks") quantity += 4;
              else if (wall[0] === "door_4x2Tracks") quantity += 4;
              else if (wall[0] === "door_5x3Tracks") quantity += 5;
              else if (wall[0] === "door_5x5Tracks") quantity += 5;
              else if (wall[0] === "door_6x6_Tracks") quantity += 6;
              else if (wall[0] === "door_6x3_Tracks") quantity += 6;
              else if (wall[0] === "door_7x7_Tracks") quantity += 7;
              else if (wall[0] === "door_7x4_Tracks") quantity += 7;
            }
          });
          total = item.price * quantity;
        }
        if (item.name === "glassdoorsdark") {
          truewalls.map((wall) => {
            if (wall[2]) {
              if (wall[0] === "door_3x3Tracks") quantity += 3;
              else if (wall[0] === "door_4x4Tracks") quantity += 4;
              else if (wall[0] === "door_4x2Tracks") quantity += 4;
              else if (wall[0] === "door_5x3Tracks") quantity += 5;
              else if (wall[0] === "door_5x5Tracks") quantity += 5;
              else if (wall[0] === "door_6x6_Tracks") quantity += 6;
              else if (wall[0] === "door_6x3_Tracks") quantity += 6;
              else if (wall[0] === "door_7x7_Tracks") quantity += 7;
              else if (wall[0] === "door_7x4_Tracks") quantity += 7;
            }
          });
          total = item.price * quantity;
        }
        if (item.name === "windowdoors") {
          truewalls.map((wall) => {
            if (!wall[2]) {
              if (wall[0] === "window_2x2Tracks") quantity += 2;
              else if (wall[0] === "window_4x4Tracks") quantity += 4;
            }
          });
          total = item.price * quantity;
        }
        if (item.name === "windowdoorsdark") {
          truewalls.map((wall) => {
            if (wall[2]) {
              if (wall[0] === "window_2x2Tracks") quantity += 2;
              else if (wall[0] === "window_4x4Tracks") quantity += 4;
            }
          });
          total = item.price * quantity;
        }
        if (item.name === "aluminiumWall") {
          truewalls.map((wall) => {
            if (wall[0] === item.name) {
              quantity += 1 * (wall[1] * houseHeight);
            }
          });
          if (this.walls)
            if (this.state?.roofType === "oase")
              this.walls.map((item) => {
                if (item.id === "v-0-0") {
                  if (item.woodWall.outerColor.startsWith("alum"))
                    if (!this.state?.spy.left) quantity += spiearea;
                } else if (item.id === `v-0-${columns - 1}`)
                  if (item.woodWall.outerColor.startsWith("alum"))
                    if (!this.state?.spy.right) quantity += spiearea;
              });
          quantity = roundUpToNearestHalf(quantity);
          total = item.price * quantity;
        }

        if (item.name === "fundering") {
          if (this.state?.optionsMaterial.fundering === "fundatiekoker")
            truewalls.map((item) => {
              if (item[0].startsWith("door") || item[0].startsWith("window"))
                quantity += roundUpToNearestHalf(item[1]);
            });
          total = item.price * quantity;
        }

        // {
        //   if (item.name === "window_2x2Tracks") {
        //     truewalls.map((wall) =>
        //       wall[0] === item.name ? (quantity += 1 * wall[1]) : quantity
        //     );
        //   }
        //   if (item.name === "window_4x4Tracks") {
        //     truewalls.map((wall) => {
        //       if (wall[0] === item.name) {
        //         quantity += 1;
        //         total += item.price * (wall[1] / 1000);
        //       }
        //     });
        //   }
        //   if (item.name === "door_3x3Tracks") {
        //     truewalls.map((wall) => {
        //       if (wall[0] === item.name) {
        //         quantity += 1;
        //         total += item.price * (wall[1] / 1000);
        //       }
        //     });
        //   }
        //   if (item.name === "door_4x4Tracks") {
        //     truewalls.map((wall) => {
        //       if (wall[0] === item.name) {
        //         quantity += 1;
        //         total += item.price * (wall[1] / 1000);
        //       }
        //     });
        //   }
        //   if (item.name === "door_4x2Tracks") {
        //     truewalls.map((wall) => {
        //       if (wall[0] === item.name) {
        //         quantity += 1;
        //         total += item.price * (wall[1] / 1000);
        //       }
        //     });
        //   }
        //   if (item.name === "door_5x3Tracks") {
        //     truewalls.map((wall) => {
        //       if (wall[0] === item.name) {
        //         quantity += 1;
        //         total += item.price * (wall[1] / 1000);
        //       }
        //     });
        //   }
        //   if (item.name === "door_5x5Tracks") {
        //     truewalls.map((wall) => {
        //       if (wall[0] === item.name) {
        //         quantity += 1;
        //         total += item.price * (wall[1] / 1000);
        //       }
        //     });
        //   }
        //   if (item.name === "door_6x6_Tracks") {
        //     truewalls.map((wall) => {
        //       if (wall[0] === item.name) {
        //         quantity += 1;
        //         total += item.price * (wall[1] / 1000);
        //       }
        //     });
        //   }
        //   if (item.name === "door_6x3_Tracks") {
        //     truewalls.map((wall) => {
        //       if (wall[0] === item.name) {
        //         quantity += 1;
        //         total += item.price * (wall[1] / 1000);
        //       }
        //     });
        //   }
        //   if (item.name === "door_7x7_Tracks") {
        //     truewalls.map((wall) => {
        //       if (wall[0] === item.name) {
        //         quantity += 1;
        //         total += item.price * (wall[1] / 1000);
        //       }
        //     });
        //   }
        //   if (item.name === "door_7x4_Tracks") {
        //     truewalls.map((wall) => {
        //       if (wall[0] === item.name) {
        //         quantity += 1;
        //         total += item.price * (wall[1] / 1000);
        //       }
        //     });
        //   }
        // }
      }
      if (lights) {
        const everyLigger = this.state?.lights.everyLigger;
        const posx = oase ? liggerWidth / 2 : houseColumnSize + liggerWidth / 2;
        const lastposx = oase
          ? houseWidth - liggerWidth / 2
          : houseWidth - houseColumnSize - liggerWidth / 2;
        const distance = lastposx - posx;
        let amount = Math.trunc(distance / (maxPanelWidth + gap)) - 1;
        if (distance / amount > maxPanelWidth) amount += 1;

        if (item.name === "spotlight") {
          quantity = everyLigger ? amount * 2 : amount - (amount % 2);
          total = quantity * item.price;
        }
      }

      //services
      if (true) {
        if (item.name === "unevenwidth") {
          quantity = houseWidth % 1 === 0 ? 0 : 1;
          total = item.price;
        }
        if (item.name === "unevendepth") {
          quantity = houseDepth % 1 === 0 ? 0 : 1;
          total = item.price;
        }
      }

      //poer
      if (true) {
        if (item.name === "poer") {
          quantity = poer ? columns : 0;
          total = quantity * item.price;
        }
        if (item.name === "hwapoer") {
          quantity = hwapoer ? columns : 0;
          total = quantity * item.price;
        }
      }

      return {
        ...item,
        quantity,
        total,
      };
    });

    return this.data;
  }

  // static getPrice = (state: any, name: string) => {
  //   let price = 0;

  //   if (name === "aludra") {
  //   }

  //   return price;
  // };
}

export default CalculationService;
